/**
 * Tidy up a phone / SSN the patient typed, ON BLUR, and only when it is already VALID.
 *
 * The point is reassurance, not correction: "5555551234" becoming "(555) 555-1234" the
 * moment the patient tabs away tells them the form understood what they typed. An invalid
 * value is returned as `null` here and left EXACTLY as typed on screen, because the patient
 * has to be able to see their own mistake next to the red message explaining it — silently
 * rewriting a half-typed number is how a patient ends up staring at a field they "already
 * fixed".
 *
 * ⛔ IMPORT-FREE ON PURPOSE — cypress/node-tests/forms-format-rules.test.js loads and
 * executes THIS EXACT FILE rather than mirroring it. Keep it dependency-free.
 */

/**
 * Every digit in the value, in order. Never throws; null/undefined -> ''.
 * @param {any} raw
 * @returns {string}
 */
export function digitsOnly(raw) {
    if (raw === null || raw === undefined) {
        return ''
    }
    return String(raw).replace(/\D/g, '')
}

/**
 * "(555) 555-1234" for a number Teamio can actually text, else null.
 *
 * Accepts exactly what src/utils/patientPhone.js accepts — 10 digits, or 11 starting with
 * a 1 (the country code, dropped for display). Anything else is not ours to reformat.
 *
 * @param {any} raw
 * @returns {string|null}
 */
export function formatPhoneForDisplay(raw) {
    const digits = digitsOnly(raw)

    let ten = ''
    if (digits.length === 10) {
        ten = digits
    } else if (digits.length === 11 && digits.charAt(0) === '1') {
        ten = digits.slice(1)
    } else {
        return null
    }

    return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`
}

/**
 * "123-45-6789" for exactly 9 digits, else null.
 *
 * A value containing a LETTER is never reformatted — "none" and "n/a" are answers, not
 * mistyped numbers, and stripping them down to digits would leave nothing.
 *
 * @param {any} raw
 * @returns {string|null}
 */
export function formatSsnForDisplay(raw) {
    if (raw === null || raw === undefined) {
        return null
    }
    if (/[a-z]/i.test(String(raw))) {
        return null
    }

    const digits = digitsOnly(raw)
    if (digits.length !== 9) {
        return null
    }

    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`
}
